import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthContext';
import { useNotificationContext } from '../../context/NotificationContext';
import { ApiService } from '../../services/api';
import {
  ProductRecognitionResponse,
  CameraImageResponse,
  ProductRecognitionBBox,
  ProductRecognitionPolygon,
  CameraPog,
  CameraDetails,
  PlanogramComplianceResponse,
  PogBBox,
  PlanogramComplianceBox,
  ProductRecognitionCropScanUpdate,
  ShapeType,
  ProductRecognitionLayerType,
} from '../../types';
import { useImagePreloader } from '../../hooks/useImagePreloader';
import { CameraImageViewer } from './components/CameraImageViewer';
import { ACTION_LAYER_CONFIG } from '../store-details/components/PlanogramComplianceControlCenter';
import { LAYER_CONFIG } from '../store-details/components/ProductRecognitionControlCenter';
import { LayersColumn } from '../../components/LayersColumn';
import { ImageBox, BoxedImage } from '../../components/BoxedImage';
import { getPlanogramComplianceActions } from '../../utils/planogramComplianceActions';
import {
  getFilteredBoxesForLayer,
  LayerType,
} from '../../utils/get-filtered-bboxex-for-layer';
import { ImageItemDetailsDialog } from '../../components/ImageItemDetailsDialog';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { PlanogramComplianceActionContent } from '../store-details/components/PlanogramComplianceActionContent';
import { useProductRecognitionScanning } from '../embeddings/utils/useProductRecognitionScanning';
import { ModFilters } from '../../components/ModFilters';
import LaunchIcon from '@mui/icons-material/Launch';

const AT_MAIN_URL = process.env.REACT_APP_AT_MAIN_URL!;

const PRODUCT_RECOGNITION_LAYER_CONFIG = LAYER_CONFIG.map((config) => {
  return {
    ...config,
    enabledByDefault: ['productBoxes', 'cartonBoxes'].includes(config.key),
  };
});

const detectionTypePerLayerType: Partial<
  Record<ProductRecognitionLayerType, ShapeType>
> = {
  cartonBoxes: ShapeType.CARTON,
  priceTagBoxes: ShapeType.PRICE_TAG,
  productBoxes: ShapeType.PRODUCT,
  shelfBoxes: ShapeType.SHELF_AREA,
  templateBoxes: ShapeType.TEMPLATE_BOX,
  templateBoxesUp: ShapeType.TEMPLATE_BOX_UP,
};

export const TaskDetailPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const [searchParams] = useSearchParams();
  const storeName = searchParams.get('store_name');
  const storeId = searchParams.get('store_id');
  const chainId = searchParams.get('chain_id');
  const tagName = searchParams.get('tag_name');
  const imageId = searchParams.get('image_id');
  const createdAt = searchParams.get('created_at');
  const osaScore = searchParams.get('osa_score');
  const complianceScore = searchParams.get('compliance_score');
  const navigate = useNavigate();
  const { token, legacyToken } = useAuthContext();
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const { createEmbeddings, startSession } = useProductRecognitionScanning(
    imageId || ''
  );
  const [taskData, setTaskData] = useState<ProductRecognitionResponse | null>(
    null
  );
  const [planogramComplianceData, setPlanogramComplianceData] =
    useState<PlanogramComplianceResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cameraImage, setCameraImage] = useState<CameraImageResponse | null>(
    null
  );
  const cameraId = searchParams.get('cameraId');

  const [, setIsLoadingCameraImage] = useState(true);
  const [cameras, setCameras] = useState<CameraDetails[]>([]);
  const [enabledMods, setEnabledMods] = useState<Set<string>>(new Set());
  const [selectedDetections, setSelectedDetections] = useState<ImageBox[]>([]);
  const [detectionForModal, setDetectionForModal] = useState<
    ImageBox | { item: PogBBox; color?: string } | null
  >(null);
  const [selectionType, setSelectionType] = useState<'pog' | 'realogram'>(
    'realogram'
  );
  const [detectionPogPairings, setDetectionPogPairings] = useState<
    Map<
      string,
      {
        detection: ImageBox;
        upc: string;
        name?: string;
        imageUri?: string;
        source: 'pog' | 'manual';
        pogBox?: PogBBox; // Only present if source is 'pog'
        timestamp: Date;
      }
    >
  >(new Map());
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [selectedIlItemIds, setSelectedIlItemIds] = useState<Set<number>>(
    new Set()
  );
  const [planogramComplianceBoxClicked, setPlanogramComplianceBoxClicked] =
    useState<PlanogramComplianceBox | null>(null);

  // Edit mode states
  const [editMode, setEditMode] = useState(false);
  const [selectedShape, setSelectedShape] = useState<ImageBox | null>(null);
  const [selectedDetectionIndex, setSelectedDetectionIndex] = useState<
    number | null
  >(null);
  const [previousEnabledLayers, setPreviousEnabledLayers] = useState<
    Set<string>
  >(new Set());
  const [editedTaskData, setEditedTaskData] =
    useState<ProductRecognitionResponse | null>(null);
  // Temporary state for drag operations (visual feedback without committing to editedTaskData)
  const [draggedDetection, setDraggedDetection] = useState<ImageBox | null>(
    null
  );

  useEffect(() => {
    const fetchTaskDetail = async () => {
      if (!taskId || !token) {
        return;
      }

      setIsLoading(true);
      try {
        const data = await ApiService.fetchTaskDetail(
          token,
          parseInt(taskId, 10)
        );
        setTaskData(data);
      } catch (err) {
        showErrorToast(
          err instanceof Error ? err.message : 'Failed to fetch task detail'
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchTaskDetail();
  }, [taskId, token, showErrorToast]);

  useEffect(() => {
    const fetchTaskPlanogramCompliance = async () => {
      if (!taskId || !token) {
        return;
      }

      try {
        const data = await ApiService.fetchTaskPlanogramCompliance(
          token,
          parseInt(taskId, 10)
        );
        setPlanogramComplianceData(data);
      } catch (err) {
        showErrorToast(
          err instanceof Error
            ? err.message
            : 'Failed to fetch planogram compliance'
        );
      }
    };

    fetchTaskPlanogramCompliance();
  }, [taskId, token, showErrorToast]);

  const fetchCameraImage = useCallback(async () => {
    if (!taskData?.imageId || !storeName) {
      return null;
    }

    try {
      const data = await ApiService.fetchCameraImage(
        token,
        storeName,
        taskData.imageId.toString()
      );
      setCameraImage(data);
    } catch (err) {
      // showErrorToast('Failed to fetch camera image');
      return null;
    }

    setIsLoadingCameraImage(false);
  }, [taskData?.imageId, storeName, token]);

  const handleBack = () => {
    navigate(-1);
  };

  // Get current image from cameraImage (equivalent to currentImage in SelectedCameraView)
  const currentImage = cameraImage?.images?.[0];

  // Use the image preloader hook
  const { preloadedImages, preloadedImagesB64 } = useImagePreloader({
    cameraTimeline: cameraImage,
    authToken: legacyToken,
    pogImageUri: undefined,
  });

  const preloadedImage = currentImage
    ? preloadedImagesB64[currentImage.imageUri]
    : undefined;

  const [enabledProductRecognitionLayers, setEnabledProductRecognitionLayers] =
    useState<Set<string>>(
      () =>
        new Set(
          PRODUCT_RECOGNITION_LAYER_CONFIG.filter(
            (l) => l.enabledByDefault
          ).map((l) => l.key)
        )
    );
  const [
    enabledPlanogramComplianceLayers,
    setEnabledPlanogramComplianceLayers,
  ] = useState<Set<string>>(
    () => new Set(ACTION_LAYER_CONFIG.map((l) => l.key))
  );
  const showProductImages = false;

  // Create a Set of detection IDs from selectedDetections for efficient lookup
  const selectedDetectionIds = useMemo(() => {
    return new Set(
      selectedDetections
        .filter((det) => det.shape)
        .map((det) => det.shape!.join(','))
    );
  }, [selectedDetections]);

  const getProductRecognitionLayerCount = useCallback(
    (layerKey: string) => {
      if (!taskData) return 0;
      const layerData = taskData[layerKey as keyof ProductRecognitionResponse];
      return Array.isArray(layerData) ? layerData.length : 0;
    },
    [taskData]
  );

  const getPlanogramComplianceLayerCount = useCallback(
    (layerKey: string) => {
      return planogramComplianceData
        ? getFilteredBoxesForLayer(
            layerKey as LayerType,
            planogramComplianceData
          ).length
        : 0;
    },
    [planogramComplianceData]
  );

  const availableProductRecognitionLayers = useMemo(() => {
    if (!editMode) {
      return PRODUCT_RECOGNITION_LAYER_CONFIG;
    }

    return PRODUCT_RECOGNITION_LAYER_CONFIG.filter(({ key }) => {
      return key.includes('Boxes') || key === 'modPolygons';
    });
  }, [editMode]);

  const toggleProductRecognitionLayer = useCallback(
    (layerKey: string) => {
      if (editMode) {
        // In edit mode, select this layer for editing instead of toggling visibility
        setEnabledProductRecognitionLayers(new Set([layerKey]));
        // Clear any previously selected shape when changing layers
        setSelectedShape(null);
        setSelectedDetectionIndex(null);
      } else {
        // Normal behavior: toggle layer visibility
        setEnabledProductRecognitionLayers((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(layerKey)) {
            newSet.delete(layerKey);
          } else {
            newSet.add(layerKey);
          }
          return newSet;
        });
      }
    },
    [editMode]
  );

  const togglePlanogramComplianceLayer = useCallback(
    (layerKey: string) => {
      setEnabledPlanogramComplianceLayers((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(layerKey)) {
          newSet.delete(layerKey);
        } else {
          newSet.add(layerKey);
        }
        return newSet;
      });
    },
    [setEnabledPlanogramComplianceLayers]
  );

  useEffect(() => {
    fetchCameraImage();
  }, [fetchCameraImage]);

  useEffect(() => {
    if (!storeName) {
      return;
    }

    (async () => {
      const cameras = await ApiService.fetchCameras(token, storeName);
      setCameras(cameras);
    })();
  }, [storeName, token]);

  // Helper function to generate a unique key from detection shape
  const getDetectionKey = (detection: ImageBox): string => {
    if (!detection.shape) return '';
    return detection.shape.join(',');
  };

  const handlePogBoxClick = useCallback(
    (pogBox: PogBBox) => {
      // Check if we have selected detections (from left-clicks)
      if (selectedDetections.length > 0) {
        // Pair all selected detections with this POG box
        setDetectionPogPairings((prev) => {
          const newMap = new Map(prev);

          selectedDetections.forEach((detection) => {
            if (!detection.shape) return;
            const detectionKey = detection.shape.join(',');

            newMap.set(detectionKey, {
              detection,
              upc: pogBox.upc,
              name: pogBox.name || undefined,
              imageUri: undefined, // We'll use POG image from pogBox
              source: 'pog',
              pogBox: pogBox,
              timestamp: new Date(),
            });
          });

          return newMap;
        });

        // Clear selected detections
        setSelectedDetections([]);

        // Show success message
        const productInfo = [
          `UPC: ${pogBox.upc}`,
          pogBox.productId ? `Product ID: ${pogBox.productId}` : null,
          pogBox.name ? `Name: ${pogBox.name}` : null,
        ]
          .filter(Boolean)
          .join(', ');

        showSuccessToast(
          `${selectedDetections.length} box(es) matched with ${productInfo}`
        );
        return;
      }

      // Default behavior: handle ilItemId selection
      const ilItemId = pogBox?.ilItemId;
      if (ilItemId === undefined || ilItemId === null) return;

      if (selectedIlItemIds.has(ilItemId)) {
        setSelectedIlItemIds(new Set());
        setPlanogramComplianceBoxClicked(null);
      } else {
        setSelectedIlItemIds(new Set([ilItemId]));
        setPlanogramComplianceBoxClicked(null);

        setSelectionType('pog');
      }
    },
    [selectedDetections, selectedIlItemIds, showSuccessToast]
  );

  const handlePogBoxRightClick = useCallback((pogBox: ImageBox | PogBBox) => {
    setDetectionForModal({ item: pogBox as PogBBox });
    setShowDetectionDialog(true);
  }, []);

  const onPlanogramComplianceClick = useCallback(
    (imageBox: ImageBox) => {
      // Extract the PlanogramComplianceBox from the ImageBox.item
      const detection = imageBox.item as PlanogramComplianceBox;
      if (!detection) return;

      // For realogram clicks, use rogItemId to select that specific item
      const rogItemId = detection?.rogItemId;
      if (rogItemId === undefined || rogItemId === null) return;

      // Toggle logic:
      if (selectedIlItemIds.has(rogItemId)) {
        setSelectedIlItemIds(new Set()); // Deselect if already selected
        setPlanogramComplianceBoxClicked(null);
      } else {
        // Realogram click: Select by rogItemId, and also add pogItemId for planogram highlighting
        const pogItemId = detection?.pogItemId;
        const idsToSelect = new Set([rogItemId]);
        if (pogItemId !== undefined && pogItemId !== null) {
          idsToSelect.add(pogItemId);
        }
        setSelectedIlItemIds(idsToSelect);
        setPlanogramComplianceBoxClicked(detection);
        setSelectionType('realogram'); // This is a realogram box click
      }
    },
    [selectedIlItemIds]
  );

  const handleManualUpcSubmit = useCallback(
    (upc: string) => {
      if (!detectionForModal) {
        showErrorToast(
          'Please select a detection box on the camera image first.'
        );
        return;
      }

      const seletecDetection = detectionForModal as ImageBox;
      const detectionKey = getDetectionKey(seletecDetection);

      // Check if UPC matches any vizSimCandidates
      const matchingCandidate = seletecDetection.item?.vizSimCandidates?.find(
        (candidate) => candidate.normalizedUpc === upc
      );

      // Store the pairing
      setDetectionPogPairings((prev) => {
        const newMap = new Map(prev);
        newMap.set(detectionKey, {
          detection: seletecDetection,
          upc: upc,
          name: matchingCandidate?.productName,
          imageUri: matchingCandidate?.productImageUri,
          source: 'manual',
          timestamp: new Date(),
        });
        return newMap;
      });

      // Show success message
      const productInfo = matchingCandidate
        ? `${matchingCandidate.productName || ''} (${upc})`
        : upc;

      // Close the dialog
      setShowDetectionDialog(false);
      setDetectionForModal(null);
      showSuccessToast(`Detection matched with ${productInfo}`);
    },
    [detectionForModal, showErrorToast, showSuccessToast]
  );

  const applyPairingsToTaskData =
    useCallback((): ProductRecognitionResponse | null => {
      if (!taskData) return null;

      // Create a deep copy of taskData
      const modifiedTaskData: ProductRecognitionResponse = JSON.parse(
        JSON.stringify(taskData)
      );

      // Helper function to get detection key
      const getShapeKey = (shape: any) => {
        if (!shape || !Array.isArray(shape)) return '';
        return shape.join(',');
      };

      Object.keys(modifiedTaskData).forEach((layerKey) => {
        const layer =
          modifiedTaskData[layerKey as keyof ProductRecognitionResponse];
        if (Array.isArray(layer)) {
          layer.forEach((detection: any) => {
            if (detection.shape) {
              const shapeKey = getShapeKey(detection.shape);
              const pairing = detectionPogPairings.get(shapeKey);

              if (pairing) {
                // Update detection with pairing data
                detection.confidence = 1;
                detection.finalUpc = pairing.upc;

                // Update or add vizSimCandidates
                if (!detection.vizSimCandidates) {
                  detection.vizSimCandidates = [];
                }

                // Find if there's already a candidate with matching UPC
                const existingCandidateIndex =
                  detection.vizSimCandidates.findIndex(
                    (candidate: any) => candidate.normalizedUpc === pairing.upc
                  );

                if (existingCandidateIndex !== -1) {
                  // Update existing candidate
                  detection.vizSimCandidates[
                    existingCandidateIndex
                  ].isConfident = true;
                  detection.vizSimCandidates[
                    existingCandidateIndex
                  ].similarity = 1;

                  // Sort vizSimCandidates by similarity descending
                  detection.vizSimCandidates.sort(
                    (a: any, b: any) =>
                      (b.similarity || 0) - (a.similarity || 0)
                  );
                } else {
                  // Add new candidate
                  detection.vizSimCandidates.unshift({
                    normalizedUpc: pairing.upc,
                    similarity: 1,
                    isConfident: true,
                    productName: pairing.name || '',
                    itemNumber: '',
                    productImageUri:
                      pairing.imageUri || pairing.pogBox?.imageUri || '',
                    productId: undefined,
                  });
                }
              }
            }
          });
        }
      });

      return modifiedTaskData;
    }, [taskData, detectionPogPairings]);

  const handlePreviewChanges = useCallback(async () => {
    if (!taskData || !token) {
      showErrorToast('Missing task data or authentication token');
      return;
    }

    const modifiedTaskData = applyPairingsToTaskData();

    if (!modifiedTaskData) {
      showErrorToast('Failed to prepare task data');
      return;
    }

    setIsPreviewLoading(true);
    try {
      const result = await ApiService.createPlanogramComplianceFromPayload(
        token,
        modifiedTaskData
      );

      setTaskData(modifiedTaskData);
      setPlanogramComplianceData(result);
      showSuccessToast('Preview created successfully');
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Failed to create preview'
      );
    } finally {
      setIsPreviewLoading(false);
    }
  }, [
    taskData,
    token,
    showErrorToast,
    showSuccessToast,
    applyPairingsToTaskData,
  ]);

  const createEmbeddingsFromPairings = useCallback(async () => {
    if (!token || !imageId) {
      return;
    }

    try {
      const cropScans = await startSession();
      const cropsToUpdate: ProductRecognitionCropScanUpdate[] = [];

      cropScans.forEach((cropScan) => {
        const bboxKey = cropScan.bbox.join(',');
        const pairing = detectionPogPairings.get(bboxKey);

        if (pairing) {
          cropsToUpdate.push({
            product_crop_scan_id: cropScan.product_crop_scan_id,
            barcode: pairing.upc,
            normalized_upc: pairing.upc,
            product_id: undefined,
          });
        }
      });

      if (cropsToUpdate.length > 0) {
        const isSuccess = await createEmbeddings(cropsToUpdate);

        if (isSuccess) {
          showSuccessToast(`Created ${cropsToUpdate.length} embeddings`);
        }
      }
    } catch {}
  }, [
    token,
    imageId,
    startSession,
    detectionPogPairings,
    createEmbeddings,
    showSuccessToast,
  ]);

  const handleSave = useCallback(async () => {
    if (!taskData || !token) {
      showErrorToast('Missing task data or authentication token');
      return;
    }

    const modifiedTaskData = applyPairingsToTaskData();
    if (!modifiedTaskData) {
      showErrorToast('Failed to prepare task data');
      return;
    }

    setIsSaveLoading(true);

    try {
      await Promise.all([
        createEmbeddingsFromPairings(),
        ApiService.createProductRecognitionFromPayload(token, modifiedTaskData),
      ]);
      showSuccessToast('Changes saved. New task created.');
    } catch (error) {
      showErrorToast(
        error instanceof Error ? error.message : 'Failed to save changes'
      );
    } finally {
      setIsSaveLoading(false);
    }
  }, [
    taskData,
    token,
    showErrorToast,
    showSuccessToast,
    applyPairingsToTaskData,
    createEmbeddingsFromPairings,
  ]);

  // This takes the compliance action status and maps to the planogram item using il_item_id.
  // Later, I use the combination of il_item_id and status to assign the correct color to the planogram
  // item.
  const planogramStatusMap = useMemo(() => {
    const map = new Map<number, string>();
    if (planogramComplianceData && planogramComplianceData.boxes) {
      for (const box of planogramComplianceData.boxes) {
        let status = box.complianceStatus;
        map.set(box.pogItemId, status);
      }
    }
    return map;
  }, [planogramComplianceData]);

  const showPogs = true;

  const selectedCamera = useMemo(() => {
    if (!cameraId || !cameras || cameras.length === 0) {
      return null;
    }
    return (
      cameras.find((cam) => cam.cameraId === parseInt(cameraId, 10)) ?? null
    );
  }, [cameraId, cameras]);

  const allTagNames = useMemo(() => {
    if (!selectedCamera) return [];
    return selectedCamera.mods.map((mod) => mod.tagName);
  }, [selectedCamera]);

  const atTaskUrl = useMemo(() => {
    return `${AT_MAIN_URL}/pog-compliance/${taskId}?storeName=${storeName}`;
  }, [storeName, taskId]);

  const currentPogs = useMemo(() => {
    const allPogs = currentImage?.ilSectionIds
      ? currentImage.ilSectionIds
          .map((id) => cameraImage?.pogs?.[id])
          .filter((pog): pog is CameraPog => !!pog)
      : [];

    return allPogs
      .filter(
        (pog): pog is CameraPog =>
          !!pog?.tagName && enabledMods.has(pog.tagName)
      )
      .sort((a, b) => {
        if (!a || !b) return 0;
        return allTagNames.indexOf(a.tagName) - allTagNames.indexOf(b.tagName);
      });
  }, [currentImage?.ilSectionIds, cameraImage?.pogs, enabledMods, allTagNames]);

  const planogramComplianceActions = useMemo(
    () =>
      planogramComplianceData
        ? getPlanogramComplianceActions(
            planogramComplianceData,
            enabledPlanogramComplianceLayers,
            ACTION_LAYER_CONFIG,
            showProductImages,
            selectedIlItemIds,
            selectionType,
            planogramComplianceBoxClicked?.rogItemId ?? undefined,
            enabledMods
          )
        : [],
    [
      planogramComplianceData,
      enabledPlanogramComplianceLayers,
      showProductImages,
      selectedIlItemIds,
      selectionType,
      planogramComplianceBoxClicked?.rogItemId,
      enabledMods,
    ]
  );

  const productRecognitionDetections = useMemo(() => {
    // Use working copy in edit mode, original taskData otherwise
    const dataSource = editMode ? editedTaskData || taskData : taskData;
    if (!dataSource) return [];

    const detections: any[] = [];

    // Add detections from each enabled layer using LAYER_CONFIG
    availableProductRecognitionLayers.forEach((layerConfig) => {
      // In edit mode, only show detections from the selected layer
      const shouldShowLayer = enabledProductRecognitionLayers.has(
        layerConfig.key
      );

      if (shouldShowLayer) {
        const layerData =
          dataSource[layerConfig.key as keyof ProductRecognitionResponse];
        if (Array.isArray(layerData)) {
          layerData.forEach(
            (
              item: ProductRecognitionBBox | ProductRecognitionPolygon,
              itemIndex: number
            ) => {
              // Skip the selected detection in edit mode - it will be rendered in DragOverlay
              if (editMode && selectedDetectionIndex === itemIndex) {
                return;
              }
              if (
                item.shape &&
                Array.isArray(item.shape) &&
                item.shape.length === 4
              ) {
                // Check if this is a polygon item
                const isPolygon =
                  'points' in item &&
                  Array.isArray(item.points) &&
                  item.points.length > 0;

                let image = null;
                if (showProductImages) {
                  image =
                    item.vizSimCandidates?.find(
                      (candidate) => candidate.isConfident
                    )?.productImageUri || null;
                }

                // Check if we need to filter by overlap with planogramComplianceBoxClicked
                if (planogramComplianceBoxClicked && 'x1' in item) {
                  if (
                    planogramComplianceBoxClicked.x1 !== item.x1 ||
                    planogramComplianceBoxClicked.y1 !== item.y1 ||
                    planogramComplianceBoxClicked.x2 !== item.x2 ||
                    planogramComplianceBoxClicked.y2 !== item.y2
                  ) {
                    return;
                  }
                } else if (selectedIlItemIds.size > 0 && 'x1' in item) {
                  const isNotMatchingAction = planogramComplianceActions.every(
                    (action) => {
                      const actionItem = action.item as ProductRecognitionBBox;
                      return (
                        actionItem.x1 !== item.x1 ||
                        actionItem.y1 !== item.y1 ||
                        actionItem.x2 !== item.x2 ||
                        actionItem.y2 !== item.y2
                      );
                    }
                  );

                  if (isNotMatchingAction) {
                    return;
                  }
                }

                const detectionKey = item.shape.join(',');
                const isPaired = detectionPogPairings.has(detectionKey);
                const isSelected = selectedDetectionIds.has(detectionKey);

                detections.push({
                  shape: item.shape,
                  color: isSelected
                    ? '#eab308'
                    : isPaired
                    ? '#f97316'
                    : layerConfig.bboxColor,
                  strokeWidth: 10,
                  item: item, // Include the original item data for dialog
                  isPolygon: isPolygon,
                  points: isPolygon ? item.points : undefined,
                  image,
                });
              }
            }
          );
        }
      }
    });

    return detections;
  }, [
    editMode,
    editedTaskData,
    taskData,
    availableProductRecognitionLayers,
    enabledProductRecognitionLayers,
    selectedDetectionIndex,
    showProductImages,
    planogramComplianceBoxClicked,
    selectedIlItemIds.size,
    detectionPogPairings,
    selectedDetectionIds,
    planogramComplianceActions,
  ]);

  const [showDetectionDialog, setShowDetectionDialog] = useState(false);

  const closeDetectionDialog = useCallback(() => {
    setShowDetectionDialog(false);
  }, []);

  const onProductRecognitionClick = (
    detection: ImageBox,
    detectionIndex: number
  ) => {
    if (!detection.shape) return;

    if (editMode) {
      // In edit mode, set the clicked detection as selectedShape for removal
      setSelectedShape(detection);
      setSelectedDetectionIndex(detectionIndex);
    } else {
      // Normal mode: toggle detection in selectedDetections array
      const detectionKey = detection.shape.join(',');
      setSelectedDetections((prev) => {
        const existingIndex = prev.findIndex(
          (det) => det.shape && det.shape.join(',') === detectionKey
        );

        if (existingIndex >= 0) {
          // Remove if already selected
          return prev.filter((_, index) => index !== existingIndex);
        } else {
          // Add if not selected
          return [...prev, detection];
        }
      });
    }
  };

  const enableEditMode = useCallback(() => {
    if (editMode) {
      return;
    }

    // Entering edit mode: save current layers and clear them, create working copy
    setPreviousEnabledLayers(new Set(enabledProductRecognitionLayers));

    const availableLayers = [...enabledProductRecognitionLayers].filter(
      (key) => {
        return key.includes('Boxes') || key === 'modPolygons';
      }
    );

    setEnabledProductRecognitionLayers(
      new Set([availableLayers[0] ?? 'productBoxes'])
    );

    // Create a deep copy of taskData for editing
    if (taskData) {
      setEditedTaskData(JSON.parse(JSON.stringify(taskData)));
    }

    setSelectedDetections([]);
    setEditMode(true);
  }, [editMode, enabledProductRecognitionLayers, taskData]);

  const disableEditMode = useCallback(() => {
    if (!editMode) {
      return;
    }

    // Exiting edit mode: restore previous layers and reset states
    setEnabledProductRecognitionLayers(previousEnabledLayers);
    setEditMode(false);
    setSelectedShape(null);
    setSelectedDetectionIndex(null);
    setEditedTaskData(null);
  }, [editMode, previousEnabledLayers]);

  const removeShape = useCallback(() => {
    if (!selectedShape || !selectedShape.item || !taskData || !editedTaskData)
      return;

    const shapeToRemove = selectedShape.item;
    const updatedEditedTaskData = { ...editedTaskData };

    // Find and remove the shape from the appropriate layer
    // We know it's in first layer since that's the only layer shown in edit mode
    const selectedLayer = [...enabledProductRecognitionLayers][0];

    if (selectedLayer) {
      const layerData =
        editedTaskData[selectedLayer as keyof ProductRecognitionResponse];
      if (Array.isArray(layerData)) {
        // Filter out the shape by comparing coordinates
        const filteredData = (
          layerData as Array<ProductRecognitionBBox | ProductRecognitionPolygon>
        ).filter((item: ProductRecognitionBBox | ProductRecognitionPolygon) => {
          // Compare by shape array
          if (
            item.shape &&
            shapeToRemove.shape &&
            Array.isArray(item.shape) &&
            Array.isArray(shapeToRemove.shape)
          ) {
            return item.shape.join(',') !== shapeToRemove.shape.join(',');
          }
          return true;
        });
        (updatedEditedTaskData as any)[selectedLayer] = filteredData;
      }
    }

    // Update taskData
    setEditedTaskData(updatedEditedTaskData);
    setSelectedShape(null);
    setSelectedDetectionIndex(null);
  }, [
    selectedShape,
    taskData,
    editedTaskData,
    enabledProductRecognitionLayers,
  ]);

  const addShape = useCallback(
    (rect: [number, number, number, number]) => {
      if (!editedTaskData) return;

      const updatedEditedTaskData = { ...editedTaskData };

      // Get the currently selected layer in edit mode
      const selectedLayer = [
        ...enabledProductRecognitionLayers,
      ][0] as ProductRecognitionLayerType;
      const detectionType = detectionTypePerLayerType[selectedLayer];

      if (!detectionType) {
        showErrorToast(`Cannot add new detection in ${selectedLayer} layer`);
        return;
      }

      if (selectedLayer) {
        const layerData =
          editedTaskData[selectedLayer as keyof ProductRecognitionResponse];
        if (Array.isArray(layerData)) {
          // Create a new shape object matching the layer type
          const [x, y, width, height] = rect;
          const newShape: ProductRecognitionBBox = {
            shape: rect,
            x1: x,
            y1: y,
            x2: x + width,
            y2: y + height,
            confidence: 1,
            vizSimCandidates: [],
            type: detectionType,
          };

          // Add the new shape to the layer
          const updatedData = [...layerData, newShape];
          (updatedEditedTaskData as any)[selectedLayer] = updatedData;
        }
      }

      // Update editedTaskData
      setEditedTaskData(updatedEditedTaskData);
    },
    [editedTaskData, enabledProductRecognitionLayers, showErrorToast]
  );

  const handleDetectionEdited = useCallback((updatedDetection: ImageBox) => {
    // During drag: only update temporary state for visual feedback
    // This avoids expensive editedTaskData updates on every mousemove
    setSelectedShape(updatedDetection);
    setDraggedDetection(updatedDetection);
  }, []);

  const handleDragEnd = useCallback(() => {
    // On drag end: commit the final changes to editedTaskData
    if (
      !draggedDetection ||
      !editedTaskData ||
      enabledProductRecognitionLayers.size !== 1 ||
      !draggedDetection.item ||
      selectedDetectionIndex === null
    )
      return;

    const updatedEditedTaskData = { ...editedTaskData };
    const selectedLayer = [...enabledProductRecognitionLayers][0];

    // Find and update the shape in the appropriate layer
    const layerData =
      editedTaskData[selectedLayer as keyof ProductRecognitionResponse];
    if (Array.isArray(layerData)) {
      // Use selectedDetectionIndex to update the correct item directly
      const updatedData = [...layerData] as Array<
        ProductRecognitionBBox | ProductRecognitionPolygon
      >;
      if (
        selectedDetectionIndex >= 0 &&
        selectedDetectionIndex < updatedData.length
      ) {
        // Update the shape at the selected index
        if (draggedDetection.isPolygon && draggedDetection.points) {
          const polygon = updatedData[
            selectedDetectionIndex
          ] as ProductRecognitionPolygon;
          polygon.points = draggedDetection.points;
          // Update shape (bounding box) and x1, y1, x2, y2
          polygon.shape = draggedDetection.shape;
          polygon.confidence = 1;
        } else {
          const bbox = updatedData[
            selectedDetectionIndex
          ] as ProductRecognitionBBox;
          const [x, y, width, height] = draggedDetection.shape;
          bbox.shape = draggedDetection.shape;
          // Update x1, y1, x2, y2 from shape [x, y, width, height]
          bbox.x1 = x;
          bbox.y1 = y;
          bbox.x2 = x + width;
          bbox.y2 = y + height;
          bbox.confidence = 1;
        }
      }
      (updatedEditedTaskData as any)[selectedLayer] = updatedData;
    }

    setEditedTaskData(updatedEditedTaskData);
    setDraggedDetection(null); // Clear temporary drag state
  }, [
    draggedDetection,
    editedTaskData,
    enabledProductRecognitionLayers,
    selectedDetectionIndex,
  ]);

  const saveDetectionChanges = useCallback(() => {
    if (!editedTaskData) return;

    // Copy working data to actual taskData
    setTaskData(editedTaskData);

    // Reset editing states
    setSelectedShape(null);
    disableEditMode();
  }, [disableEditMode, editedTaskData]);

  const cancelDetectionChanges = useCallback(() => {
    // Restore editedTaskData from taskData to undo changes
    if (taskData) {
      setEditedTaskData(JSON.parse(JSON.stringify(taskData)));
    }

    disableEditMode();
  }, [disableEditMode, taskData]);

  useEscapeKey(closeDetectionDialog);

  const onBoxClick = () => {};

  const onBoxClear = useCallback(() => {
    setSelectedShape(null);
    setSelectedDetectionIndex(null);
  }, []);

  const onProductRecognitionRightClick = (detection: ImageBox | PogBBox) => {
    setDetectionForModal(detection as ImageBox);
    setShowDetectionDialog(true);
  };

  useEffect(() => {
    setEnabledMods(new Set(allTagNames));
  }, [allTagNames]);

  const toggleMod = (tagName: string) => {
    setEnabledMods((prev) => {
      const newMods = new Set(prev);
      if (newMods.has(tagName)) {
        newMods.delete(tagName);
      } else {
        newMods.add(tagName);
      }
      return newMods;
    });
  };

  return (
    <div className="h-screen bg-gray-50 p-4 flex flex-col">
      <div className="w-full mx-auto space-y-4 flex flex-col flex-1 min-h-0">
        <div className="bg-white shadow-sm border-b rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-xl font-bold">Task Detail</h1>
              <p className="text-gray-600 text-sm"></p>
              <p className="text-gray-600 text-sm">
                Task ID: {taskId}
                {storeName && ` • Store: ${storeName}`}
                {tagName && ` • Tag: ${tagName}`}
                {imageId && ` • Image ID: ${imageId}`}
                {createdAt &&
                  ` • Created: ${new Date(createdAt).toLocaleString()}`}
                {osaScore && ` • OSA Score: ${parseFloat(osaScore).toFixed(2)}`}
                {complianceScore &&
                  ` • Compliance Score: ${parseFloat(complianceScore).toFixed(
                    2
                  )}`}
              </p>
            </div>
            <button
              onClick={handleBack}
              className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md"
            >
              Back to Tasks
            </button>
          </div>
          <div className="flex items-center gap-4">
            <ModFilters
              allTagNames={allTagNames}
              enabledMods={enabledMods}
              toggleMod={toggleMod}
              planogramComplianceData={planogramComplianceData}
            />
          </div>
        </div>

        {taskData && preloadedImage ? (
          <>
            <CameraImageViewer
              preloadedImage={preloadedImage}
              filteredBboxes={[]}
              boxActions={[]}
              productRecognitionDetections={productRecognitionDetections}
              planogramComplianceActions={planogramComplianceActions}
              showProductImages={showProductImages}
              onBoxClick={onBoxClick}
              onProductRecognitionClick={onProductRecognitionClick}
              onProductRecognitionRightClick={onProductRecognitionRightClick}
              onPlanogramComplianceClick={onPlanogramComplianceClick}
              showPogs={showPogs}
              currentPogs={currentPogs}
              preloadedImagesB64={preloadedImagesB64}
              selectedIlItemIds={selectedIlItemIds}
              planogramStatusMap={planogramStatusMap}
              onPogBoxClick={handlePogBoxClick}
              onPogBoxRightClick={handlePogBoxRightClick}
              editMode={editMode}
              selectedShape={selectedShape}
              selectedDetectionIndex={selectedDetectionIndex}
              onEditModeEnable={enableEditMode}
              onRemoveShape={removeShape}
              onSaveChanges={saveDetectionChanges}
              onCancelChanges={cancelDetectionChanges}
              onDetectionEdited={handleDetectionEdited}
              onMouseUpWhileDragging={handleDragEnd}
              onBoxClear={onBoxClear}
              onNewShapeCreated={addShape}
            />

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <LayersColumn
                title="Detection Layers"
                layer_config={availableProductRecognitionLayers}
                enabledLayers={enabledProductRecognitionLayers}
                setEnabledLayers={setEnabledProductRecognitionLayers}
                getLayerCount={getProductRecognitionLayerCount}
                toggleLayer={toggleProductRecognitionLayer}
              />
              <div>
                <LayersColumn
                  title="Action Layers"
                  layer_config={ACTION_LAYER_CONFIG}
                  enabledLayers={enabledPlanogramComplianceLayers}
                  setEnabledLayers={setEnabledPlanogramComplianceLayers}
                  getLayerCount={getPlanogramComplianceLayerCount}
                  toggleLayer={togglePlanogramComplianceLayer}
                />
                {planogramComplianceBoxClicked && (
                  <div className="mt-4">
                    <PlanogramComplianceActionContent
                      item={planogramComplianceBoxClicked}
                      cameraImageElement={
                        currentImage
                          ? preloadedImages[currentImage.imageUri]
                          : undefined
                      }
                    />
                  </div>
                )}
              </div>

              <div className="bg-white rounded-lg shadow-md p-4">
                <h3 className="text-lg font-semibold mb-4">
                  Matched Detections ({detectionPogPairings.size})
                </h3>
                {detectionPogPairings.size === 0 ? (
                  <p className="text-gray-500 text-sm">
                    No detections matched yet. Select a detection box on the
                    camera image, then click a POG box to match them.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {Array.from(detectionPogPairings.values()).map(
                      (pairing) => {
                        const detectionKey = getDetectionKey(pairing.detection);
                        const cameraImageElement = currentImage
                          ? preloadedImages[currentImage.imageUri]
                          : undefined;

                        return (
                          <div
                            key={detectionKey}
                            className="flex gap-4 p-4 border rounded-lg bg-gray-50"
                          >
                            {/* Before (Detection) */}
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-700 mb-2">
                                Before (Detection)
                              </div>
                              <div className="bg-white rounded border p-2 mb-2">
                                {cameraImageElement &&
                                pairing.detection.shape ? (
                                  <BoxedImage
                                    image={cameraImageElement}
                                    crop={pairing.detection.shape}
                                    cropPadding={20}
                                    boxes={[
                                      {
                                        shape: pairing.detection.shape,
                                        color:
                                          pairing.detection.color || '#3b82f6',
                                        strokeWidth: 5,
                                        isPolygon: pairing.detection.isPolygon,
                                        points: pairing.detection.points,
                                      },
                                    ]}
                                  />
                                ) : (
                                  <div className="w-full h-32 bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
                                    No image
                                  </div>
                                )}
                              </div>
                              <div className="text-xs space-y-1">
                                <div>
                                  <span className="font-medium">UPC:</span>{' '}
                                  {pairing.detection.item?.vizSimCandidates?.[0]
                                    ?.normalizedUpc || 'N/A'}
                                </div>
                              </div>
                            </div>

                            {/* Arrow */}
                            <div className="flex items-center justify-center px-2">
                              <svg
                                className="w-6 h-6 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                                />
                              </svg>
                            </div>

                            {/* After (Matched Product) */}
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-700 mb-2">
                                After (
                                {pairing.source === 'pog' ? 'POG' : 'Manual'})
                              </div>
                              <div className="bg-white rounded border p-2 mb-2">
                                {pairing.source === 'pog' &&
                                pairing.pogBox?.imageUri ? (
                                  <img
                                    src={pairing.pogBox.imageUri}
                                    alt={pairing.name || pairing.upc}
                                    className="w-full h-32 object-contain"
                                  />
                                ) : pairing.source === 'manual' &&
                                  pairing.imageUri ? (
                                  <img
                                    src={pairing.imageUri}
                                    alt={pairing.name || pairing.upc}
                                    className="w-full h-32 object-contain"
                                  />
                                ) : (
                                  <div className="w-full h-32 bg-gray-200 flex items-center justify-center text-gray-500 text-sm">
                                    No image
                                  </div>
                                )}
                              </div>
                              <div className="text-xs space-y-1">
                                <div>
                                  <span className="font-medium">UPC:</span>{' '}
                                  {pairing.upc}
                                </div>
                                <div>
                                  <span className="font-medium">Name:</span>{' '}
                                  {pairing.name || 'N/A'}
                                </div>
                              </div>
                            </div>

                            {/* Delete button */}
                            <div className="flex items-start">
                              <button
                                onClick={() => {
                                  setDetectionPogPairings((prev) => {
                                    const newMap = new Map(prev);
                                    newMap.delete(detectionKey);
                                    return newMap;
                                  });
                                }}
                                className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Remove pairing"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                {
                  <div className="flex gap-3 justify-between mt-6 pt-4 border-t">
                    <a
                      href={atTaskUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline font-medium flex items-center gap-1"
                    >
                      See in AT
                      <LaunchIcon fontSize="small" />
                    </a>
                    <div className="flex gap-3">
                      <button
                        onClick={handlePreviewChanges}
                        disabled={isPreviewLoading || isSaveLoading}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isPreviewLoading && (
                          <svg
                            className="animate-spin h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        )}
                        {isPreviewLoading ? 'Previewing...' : 'Preview changes'}
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={isPreviewLoading || isSaveLoading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isSaveLoading && (
                          <svg
                            className="animate-spin h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                        )}
                        {isSaveLoading ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                }
              </div>
            </div>
          </>
        ) : (
          <div>Loading image</div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6 flex-1">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                <p className="text-gray-500">Loading task detail...</p>
              </div>
            </div>
          ) : (
            !taskData && (
              <p className="text-gray-500 text-center">
                No task data available
              </p>
            )
          )}
        </div>
      </div>

      {/* Detection Details Dialog */}
      {detectionForModal && (
        <ImageItemDetailsDialog
          item={detectionForModal.item}
          cameraImageElement={
            currentImage ? preloadedImages[currentImage.imageUri] : undefined
          }
          isOpen={showDetectionDialog}
          onClose={closeDetectionDialog}
          layerBboxColor={detectionForModal.color}
          chainId={chainId ? Number.parseInt(chainId) : undefined}
          storeId={storeId ? Number.parseInt(storeId) : undefined}
          onUpcSubmit={handleManualUpcSubmit}
        />
      )}
    </div>
  );
};

export default TaskDetailPage;
